import { useCategoryFamilyMatrix } from "../hooks/use-intelligence-queries.ts";
import {
  CATEGORY_FAMILIES,
  type CategoryFamily,
  type CategoryFamilyMatrixCell,
  SCORE_BANDS,
  type ScoreBand,
} from "../types/index.ts";

const LOW_SAMPLE_THRESHOLD = 5;

function formatDollars(n: number | null): string {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface CellIndex {
  [family: string]: { [band: string]: CategoryFamilyMatrixCell };
}

function buildIndex(cells: CategoryFamilyMatrixCell[]): CellIndex {
  const idx: CellIndex = {};
  for (const c of cells) {
    if (!idx[c.family]) idx[c.family] = {};
    const familyBands = idx[c.family];
    if (familyBands) familyBands[c.band] = c;
  }
  return idx;
}

export function CategoryFamilyMatrix() {
  const { data, isLoading, error } = useCategoryFamilyMatrix();

  if (isLoading) return <div className="intel-loading">Loading category matrix…</div>;
  if (error || !data) return <div className="intel-error">Failed to load category matrix</div>;

  const index = buildIndex(data);

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <h3 className="intel-section__title">Median raise by category × score band</h3>
        <p className="intel-section__subtitle">
          Negotiation anchor: the median raise inside each cell tells you what a comparable deal in
          the same family + score band raised. Cells with n&nbsp;&lt;&nbsp;5 are shaded as
          low-sample and shouldn't be used as a hard anchor.
        </p>
      </header>
      <div className="intel-matrix-wrap">
        <table className="intel-matrix">
          <thead>
            <tr>
              <th className="intel-matrix__family-head">Family</th>
              {SCORE_BANDS.map((band) => (
                <th key={band} className="intel-matrix__band-head">
                  {band}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CATEGORY_FAMILIES.map((family) => (
              <FamilyRow key={family} family={family} index={index} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FamilyRow({ family, index }: { family: CategoryFamily; index: CellIndex }) {
  return (
    <tr>
      <td className="intel-matrix__family-cell">{family}</td>
      {SCORE_BANDS.map((band) => {
        const cell = index[family]?.[band];
        return <MatrixCell key={band} cell={cell} band={band} />;
      })}
    </tr>
  );
}

function MatrixCell({
  cell,
  band,
}: {
  cell: CategoryFamilyMatrixCell | undefined;
  band: ScoreBand;
}) {
  if (!cell || cell.projectCount === 0) {
    return (
      <td className="intel-matrix__cell intel-matrix__cell--empty" aria-label={`${band}: no data`}>
        —
      </td>
    );
  }

  // Low-sample flag keys off the funded sample — that's what actually
  // contributes to the median. A cell with 20 projects but only 3
  // funded has a 3-sample median, and the flag should reflect that.
  const lowSample = cell.fundedCount < LOW_SAMPLE_THRESHOLD;
  const tooltip = lowSample
    ? `Low sample — ${cell.fundedCount} funded of ${cell.projectCount} projects`
    : `${cell.fundedCount} funded of ${cell.projectCount} projects`;

  return (
    <td
      className={`intel-matrix__cell${lowSample ? " intel-matrix__cell--low-sample" : ""}`}
      title={tooltip}
    >
      <div className="intel-matrix__median">{formatDollars(cell.medianRaiseUsd)}</div>
      <div className="intel-matrix__n">
        n={cell.fundedCount}/{cell.projectCount}
      </div>
    </td>
  );
}
